import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import CustomerFilter from "../section/CustomerFilter";
import CustomerTable from "../section/CustomerTable";

import {
  Users,
  Building2,
  Store,
  Star,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/customers`;

export default function Customer() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [branch, setBranch] = useState("");
  const [favOnly, setFavOnly] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const handleReset = () => {
    setSearch("");
    setBranch("");
    setFavOnly(false);
  };

  const toggleFav = async (id, current) => {
    try {
      await axios.patch(`${API_URL}/${id}/favourite`, { favourite: !current });
      setCustomers((prev) => prev.map((c) => (c.id === id ? { ...c, favourite: !current } : c)));
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddCustomer = () => {
    navigate("/add-customer");
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(API_URL);

      if (response.data.success) {
        setCustomers(response.data.customers || []);
      } else {
        setCustomers([]);
      }
    } catch (err) {
      console.error(err);

      setError("Failed to load customers.");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const searchMatch =
        search === "" ||
        customer.customer_name
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        customer.phone
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        customer.customer_id
          ?.toLowerCase()
          .includes(search.toLowerCase());

      const branchMatch =
        branch === "" || customer.branch === branch;

      const favMatch = !favOnly || customer.favourite;

      return searchMatch && branchMatch && favMatch;
    });
  }, [customers, search, branch, favOnly]);

  const totalCustomers = filteredCustomers.length;

  const zaOpticalCount = filteredCustomers.filter(
    (customer) => customer.branch === "Z.A. Optical Johripur"
  ).length;

  const futureVisionCount = filteredCustomers.filter(
    (customer) => customer.branch === "Z.A. Optical Jhilmil"
  ).length;
    return (
    <div className="space-y-6">

      {/* Filter */}
      <CustomerFilter
        search={search}
        setSearch={setSearch}
        branch={branch}
        setBranch={setBranch}
        onReset={handleReset}
        onAddCustomer={handleAddCustomer}
      />

      {/* Favourites toggle */}
      <div className="flex justify-end">
        <button
          onClick={() => setFavOnly((v) => !v)}
          data-testid="customer-fav-filter"
          className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition ${favOnly ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
        >
          <Star size={16} className={favOnly ? "fill-amber-400 text-amber-400" : ""} />
          {favOnly ? "Showing Favourites" : "Favourites only"}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">

        {/* Total Customers */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md">
          <div className="flex items-center justify-between">

            <div>
              <p className="text-sm font-medium text-slate-500">
                Total Customers
              </p>

              <h2 className="mt-3 text-4xl font-bold text-slate-900">
                {totalCustomers}
              </h2>

              <p className="mt-2 text-sm text-slate-400">
                All Registered Customers
              </p>
            </div>

            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-100">
              <Users
                className="text-blue-600"
                size={30}
              />
            </div>

          </div>
        </div>

        {/* ZA Optical */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md">
          <div className="flex items-center justify-between">

            <div>
              <p className="text-sm font-medium text-slate-500">
                Z.A. Optical Johripur
              </p>

              <h2 className="mt-3 text-4xl font-bold text-slate-900">
                {zaOpticalCount}
              </h2>

              <p className="mt-2 text-sm text-slate-400">
                Johripur Customers
              </p>
            </div>

            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100">
              <Store
                className="text-emerald-600"
                size={30}
              />
            </div>

          </div>
        </div>

        {/* Future Vision */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md">
          <div className="flex items-center justify-between">

            <div>
              <p className="text-sm font-medium text-slate-500">
                Z.A. Optical Jhilmil
              </p>

              <h2 className="mt-3 text-4xl font-bold text-slate-900">
                {futureVisionCount}
              </h2>

              <p className="mt-2 text-sm text-slate-400">
                Jhilmil Customers
              </p>
            </div>

            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-100">
              <Building2
                className="text-violet-600"
                size={30}
              />
            </div>

          </div>
        </div>

      </div>

      {/* Loading */}
      {loading && (
        <div className="rounded-3xl border border-slate-200 bg-white p-16 text-center shadow-sm">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600"></div>

          <p className="mt-5 text-slate-500">
            Loading customers...
          </p>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="font-medium text-red-600">
            {error}
          </p>
        </div>
      )}

      {/* Customer Table */}
{!loading && !error && (
  <CustomerTable
    customers={filteredCustomers}
    onToggleFav={toggleFav}
    onView={(id) => navigate(`/customer/${id}`)}
    onEdit={(id) => navigate(`/edit-customer/${id}`)}
    onDelete={async (id) => {
      if (!window.confirm("Are you sure you want to delete this customer?")) return;
      try {
        await axios.delete(`${API_URL}/${id}`);
        fetchCustomers();
      } catch (err) {
        console.error(err);
        alert(err.response?.data?.message || "Failed to delete customer.");
      }
    }}
  />
)}

    </div>
  );
}